import React from 'react';
import { Engine, Scene, HemisphericLight, Vector3, SceneLoader } from '@babylonjs/core';
import * as BABYLON from '@babylonjs/core'
import '@babylonjs/loaders'; // Import loaders for .glb files
import ReactAudioPlayer from 'react-audio-player';

import isMobile from './helpers/isMobileDevice';

const msgOffset = new Vector3(0, 1, -3);

interface Scene3D {
  canvasRef: any,
  coinSoundRef: any,
  failSoundRef: any,
  jumpSoundRef: any,
  slideSoundRef: any,
  winSoundRef: any,

  engineGlobal: Engine | null,
  sceneGlobal: Scene | null,
  gamepadManagerGlobal: BABYLON.GamepadManager | null,
  nativeGamepadGlobal: Gamepad | null,
  gamepadGlobal: BABYLON.Gamepad | null,

  showBoundingBoxes: boolean
  cameraGlobal: BABYLON.FreeCamera | null,
  modulesRoot: BABYLON.Mesh | null,
  modules: BABYLON.Mesh[] | null,
  invincibleAnimation: number

  obstacles: BABYLON.Mesh[] | null,
  coins: BABYLON.Mesh[] | null,

  last3ModulesUsed: number[],
  characterRoot: BABYLON.Mesh | null,
  characterMesh: BABYLON.Mesh | null,
  characterCollider: BABYLON.Mesh | null,

  coinScorePlus1: BABYLON.Mesh | null,
  coinScorePlus3: BABYLON.Mesh | null,
  coinScorePlus5: BABYLON.Mesh | null,

  goodCoinsMsgs: BABYLON.Mesh[] | null,
  badCoinsMsgs: BABYLON.Mesh[] | null,

  infiniteRoadNextCheck: number,
  infiniteRoadOffsetCheck: number,

  shadowGenerator: BABYLON.ShadowGenerator | null,

  characterSideDistance: number,
  characterSideAnimationName: string,
  characterSideAnimationValue: number,
  characterSpeed: number,
  characterBaseSpeed: number,
  characterLane: number,
  characterVerticalAnimationName: string

  characterAnimationIdle: BABYLON.AnimationGroup | null
  characterAnimationWalk: BABYLON.AnimationGroup | null
  characterAnimationRun: BABYLON.AnimationGroup | null
  characterAnimationSlide: BABYLON.AnimationGroup | null
  characterAnimationHit: BABYLON.AnimationGroup | null
  characterAnimationJump: BABYLON.AnimationGroup | null

  totalGameTime: number
  totalScore: number
  totalLife: number

  gamePaused: boolean

  activeFlyingScores: FlyingScore[],

  globalPerformanceControl: {
    scalingLevel: number
    framesCount: number
    avgFrameTime: number
    minScalingLevel: number
    maxScalingLevel: number
    targetFramerate: number
    frameSamples: number
  }
}

interface Props {
  isVisible: boolean,
  playGame: boolean,
  gameOver: boolean,
  isWindowFocused: boolean,
  soundMuted: boolean,
  horizontalMovement: number,
  verticalMovement: number,
  setHorizontalMovement: (value: number) => void,
  setVerticalMovement: (value: number) => void,
  setUserScore: (value: number) => void,
  setUserTime: (value: number) => void,
  setUserLifes: (value: number) => void,
  setIsGameLoaded: (value: boolean) => void,
}

interface State {
  somestate: any,
}

interface FlyingScore{
  timeLeft: number,
  mesh: BABYLON.Mesh,
  coin: BABYLON.Mesh
}

class Scene3D extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      somestate: {},
    }
    this.canvasRef = React.createRef()
    this.coinSoundRef = React.createRef()
    this.failSoundRef = React.createRef()
    this.jumpSoundRef = React.createRef()
    this.slideSoundRef = React.createRef()
    this.winSoundRef = React.createRef()

    this.sceneGlobal = null
    this.gamepadGlobal = null
    this.showBoundingBoxes = false
    this.modulesRoot = null
    this.modules = null
    this.invincibleAnimation = 0.2
    this.characterCollider = null

    this.coinScorePlus1 = null
    this.coinScorePlus3 = null
    this.coinScorePlus5 = null

    this.goodCoinsMsgs = null
    this.badCoinsMsgs = null

    this.activeFlyingScores = []

    this.obstacles = null
    this.coins = null

    this.characterRoot = null
    this.characterMesh = null

    this.infiniteRoadNextCheck = 100
    this.infiniteRoadOffsetCheck = 10

    this.shadowGenerator = null

    this.characterSideDistance = 2.5
    this.characterSideAnimationName = "free"
    this.characterSideAnimationValue = 0
    this.characterSpeed = 0
    this.characterBaseSpeed = 10
    this.characterLane = 0

    this.characterVerticalAnimationName = "free"

    this.characterAnimationIdle = null
    this.characterAnimationWalk = null
    this.characterAnimationRun = null
    this.characterAnimationSlide = null

    this.characterAnimationHit = null
    this.characterAnimationJump = null

    this.totalGameTime = 0
    this.totalScore = 0
    this.totalLife = 3
    this.gamePaused = true

    this.globalPerformanceControl = {
      scalingLevel: 1,
      framesCount: 0,
      avgFrameTime: 0,
      minScalingLevel: 1.0,//quality at original size
      maxScalingLevel: 1.5,//quality at half size
      targetFramerate: 30,
      frameSamples: 60,
    }
  }

  componentDidMount(): void {
    if (this.canvasRef !== null) {
      this.CreateScene();
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.horizontalMovement !== this.props.horizontalMovement && this.props.horizontalMovement !== 0) {
      this.AnimateCharacterHorizontally()
    }
    if (prevProps.verticalMovement !== this.props.verticalMovement && this.props.verticalMovement !== 0) {
      this.AnimateCharacterVerically()
    }
  }

  componentWillUnmount(): void {
    if (this.gamepadManagerGlobal){
      this.gamepadManagerGlobal.onGamepadConnectedObservable.clear();
      this.gamepadManagerGlobal.onGamepadDisconnectedObservable.clear();
    }
    if (this.sceneGlobal)
      this.sceneGlobal.dispose()
    if (this.engineGlobal)
      this.engineGlobal.dispose()
  }

  CreateScene = () => {
    const engine = new Engine(this.canvasRef.current, true)
    const scene = new Scene(engine);
    const gamepadManager = new BABYLON.GamepadManager();
    this.engineGlobal = engine
    this.sceneGlobal = scene
    this.gamepadManagerGlobal = gamepadManager;

    // gamepadManager.onGamepadConnectedObservable.add((gamepad: BABYLON.Gamepad, state: BABYLON.EventState) => {
    //  console.log("Gamepad conectado:", gamepad.id);
    //   if (gamepad instanceof BABYLON.Xbox360Pad) {
    //       //Xbox button down/up events
    //       gamepad.onButtonDownObservable.add(this.onGamepadButtonDown);
    //       gamepad.onButtonUpObservable.add(this.onGamepadButtonUp);
    //       //d-pad
    //       gamepad.ondpaddown(this.onGamepadDpadDown);
    //       gamepad.ondpadup(this.onGamepadDpadUp);
    //       //sticks
    //       gamepad.onleftstickchanged(this.onGamepadLeftStickChanged);
    //       gamepad.onrightstickchanged(this.onGamepadRightStickChanged);
    //   }
    // });

    // gamepadManager.onGamepadDisconnectedObservable.add((gamepad) => {
    //   console.log("Gamepad desconectado:", gamepad.id);
    // });

    scene.clearColor = new BABYLON.Color4(145 / 255, 196 / 255, 1, 1);
    scene.ambientColor = new BABYLON.Color3(1, 1, 1);//NO SIRVE!!!

    scene.fogMode = BABYLON.Scene.FOGMODE_LINEAR;
    scene.fogStart = 140.0;
    scene.fogEnd = 170.0;
    scene.fogColor = new BABYLON.Color3(0.8, 0.8, 0.8);

    var camera = new BABYLON.FreeCamera("MainCamera", new BABYLON.Vector3(0, 3.2, 8), scene);
    this.cameraGlobal = camera
    camera.fov = window.innerWidth > window.innerHeight ? 0.8 : 1.0
    camera.setTarget(new Vector3(0, 1, 0))

    var defaultPipeline = new BABYLON.DefaultRenderingPipeline("default", false, scene, [camera]);
    defaultPipeline.imageProcessing.contrast = 2.7;//3

    if (isMobile() === true) {
      engine.disableUniformBuffers = true;
      defaultPipeline.samples = 1 //AA
      engine.setHardwareScalingLevel(1.5); //default 1, higher value is lower resolution -_-
      // var shadowGenerator = new BABYLON.ShadowGenerator(2048, directionalLight);//2048 | 256
      // this.shadowGenerator = shadowGenerator
      // shadowGenerator.useBlurExponentialShadowMap = true;
      // shadowGenerator.depthScale = 2500;//2500 |
      // shadowGenerator.bias = 0.01;//0.001 |
    }

    // Configure Draco Loading
    BABYLON.DracoCompression.Configuration.decoder = {
      wasmUrl: "./draco/draco_decoder.js",
      wasmBinaryUrl: "./draco/draco_decoder.wasm",
      fallbackUrl: "./draco/draco_wasm_wrapper.js",
    };

    var ambientLight = new HemisphericLight("GeneralLight", new Vector3(0, 1, 0), scene);
    ambientLight.intensity = 1.5; //2

    this.LoadAllTexturesAndModels().then(() => { })//async

    scene.onBeforeRenderObservable.add(() => {
      this.frameRender(0.001 * scene.deltaTime)
    });

    engine.runRenderLoop(() => {
      scene.render();
    });

    window.addEventListener("resize", function () {
      camera.fov = window.innerWidth > window.innerHeight ? 0.8 : 1.0
      engine.resize();
    });
  }

  onGamepadButtonDown = (button: BABYLON.Xbox360Button, state: BABYLON.EventState): void => {
    //console.log(BABYLON.Xbox360Button[button] + " pressed up");
    if(BABYLON.Xbox360Button[button] === 'A'){
      this.props.setVerticalMovement(1);
    }
  }
  onGamepadButtonUp = (button: BABYLON.Xbox360Button, state: BABYLON.EventState): void => {
    if(BABYLON.Xbox360Button[button] === 'A'){
      this.props.setVerticalMovement(0);
    }
  }
  onGamepadDpadDown = (dPad: BABYLON.Xbox360Dpad): void => {
    //console.log(BABYLON.Xbox360Dpad[dPad] + " pressed up");
    if(BABYLON.Xbox360Dpad[dPad] === 'Left'){
      this.props.setHorizontalMovement(1);
    }else if(BABYLON.Xbox360Dpad[dPad] === 'Right'){
      this.props.setHorizontalMovement(-1);
    }else if(BABYLON.Xbox360Dpad[dPad] === 'Up'){
      this.props.setVerticalMovement(1);
    }else if(BABYLON.Xbox360Dpad[dPad] === 'Down'){
      this.props.setVerticalMovement(-1);
    }
  }
  onGamepadDpadUp = (dPad: BABYLON.Xbox360Dpad): void => {
    if(BABYLON.Xbox360Dpad[dPad] === 'Left'){
      this.props.setHorizontalMovement(0);
    }else if(BABYLON.Xbox360Dpad[dPad] === 'Right'){
      this.props.setHorizontalMovement(0);
    }else if(BABYLON.Xbox360Dpad[dPad] === 'Up'){
      this.props.setVerticalMovement(0);
    }else if(BABYLON.Xbox360Dpad[dPad] === 'Down'){
      this.props.setVerticalMovement(0);
    }
  }
  onGamepadLeftStickChanged = (values: BABYLON.StickValues): void => {
    //console.log(values.x + " | " + values.y);
    if(values.x < -0.7){
      this.props.setHorizontalMovement(1);
    }else if(values.x > 0.7){
      this.props.setHorizontalMovement(-1);
    }else if (this.props.horizontalMovement !== 0){
      this.props.setHorizontalMovement(0);
      console.log("reset horizontal");
    }
      
    if(values.y < -0.7){
      this.props.setVerticalMovement(1);
    }else if(values.y > 0.7){
      this.props.setVerticalMovement(-1);
    }else if (this.props.verticalMovement !== 0){
      this.props.setVerticalMovement(0);
      console.log("reset vertical");
    }
  }
  onGamepadRightStickChanged = (values: BABYLON.StickValues): void => {
    //console.log(values.x + " | " + values.y);
  }

  LoadCubeMapAsync = async (texturesUrl: string, scene: BABYLON.Scene): Promise<BABYLON.CubeTexture> => {
    return new Promise((resolve, reject) => {
      const cubeTexture = new BABYLON.CubeTexture(texturesUrl, scene, null, true, null,
        () => { resolve(cubeTexture) },
        (message, exception) => reject(new Error("Error loadingcube texture: " + message))
      )
    })
  }

  LoadTextureAsync = async (textureUrl: string, scene: BABYLON.Scene, invertY: boolean): Promise<BABYLON.Texture> => {
    return new Promise((resolve, reject) => {
      const texture: BABYLON.Texture = new BABYLON.Texture(textureUrl, scene, true, invertY, BABYLON.Texture.BILINEAR_SAMPLINGMODE,
        () => resolve(texture),
        (message, exception) => reject(new Error("Error loading texture: " + message))
      );
    })
  }


  LoadAllTexturesAndModels = async (): Promise<void> => {
    if (this.sceneGlobal !== null) {

      console.log("Loading Environment")
      //LOAD ENVIRONMENT
      const cubeTexture = await this.LoadCubeMapAsync('./models/ciudad/1', this.sceneGlobal);
      this.sceneGlobal.environmentTexture = cubeTexture
      this.sceneGlobal.environmentIntensity = 0.5

      //LOAD BACKGROUND PLANE
      const planeBackgroundMaterial = new BABYLON.StandardMaterial('groundMaterial', this.sceneGlobal);
      planeBackgroundMaterial.diffuseTexture = await this.LoadTextureAsync('./models/background/Fondo2.jpg', this.sceneGlobal, true);
      planeBackgroundMaterial.disableLighting = true
      planeBackgroundMaterial.emissiveColor = new BABYLON.Color3(1,1,1)
      const planeBackground = BABYLON.MeshBuilder.CreateGround("ground", { height: 1.5, width: 2.5 });
      planeBackground.rotation = new Vector3(0.5 * Math.PI, 0, 0)
      planeBackground.scaling = new Vector3(105, 1.5, 37.5)
      planeBackground.position = new Vector3(0, 16.5, -150)
      planeBackground.material = planeBackgroundMaterial;

      this.goodCoinsMsgs = [];
      this.badCoinsMsgs = [];
      const badMsgs = ["negative-1.png","negative-2.png","negative-3.png"];
      const goodMsgs = ["positive-1.png","positive-2.png","positive-3.png","positive-4.png","positive-5.png","positive-6.png","positive-7.png","positive-8.png","positive-9.png","positive-10.png"];

      for (const fineName of goodMsgs){
        const msgMaterial = new BABYLON.StandardMaterial('goodMat', this.sceneGlobal);
        msgMaterial.diffuseTexture = await this.LoadTextureAsync('./models/scores/' + fineName, this.sceneGlobal, false);
        const height = 0.4;
        const width = height * msgMaterial.diffuseTexture.getSize().width / msgMaterial.diffuseTexture.getSize().height;
        msgMaterial.transparencyMode = 2
        msgMaterial.disableLighting = true
        msgMaterial.diffuseTexture.hasAlpha = true
        msgMaterial.useAlphaFromDiffuseTexture = true;
        msgMaterial.emissiveColor = new BABYLON.Color3(1,1,1)
        const msgMesh = BABYLON.MeshBuilder.CreateGround("goodMesh", { height: height, width: width });
        msgMesh.rotation = new Vector3(0.5 * Math.PI, 0, 0)
        msgMesh.material = msgMaterial;
        msgMesh.setEnabled(false);
        this.goodCoinsMsgs.push(msgMesh);
      }
      for (const fineName of badMsgs){
        const msgMaterial = new BABYLON.StandardMaterial('badMat', this.sceneGlobal);
        msgMaterial.diffuseTexture = await this.LoadTextureAsync('./models/scores/' + fineName, this.sceneGlobal, false);
        const height = 0.4;
        const width = height * msgMaterial.diffuseTexture.getSize().width / msgMaterial.diffuseTexture.getSize().height;
        msgMaterial.transparencyMode = 2
        msgMaterial.disableLighting = true
        msgMaterial.diffuseTexture.hasAlpha = true
        msgMaterial.useAlphaFromDiffuseTexture = true;
        msgMaterial.emissiveColor = new BABYLON.Color3(1,1,1)
        const msgMesh = BABYLON.MeshBuilder.CreateGround("badMesh", { height: height, width: width });
        msgMesh.rotation = new Vector3(0.5 * Math.PI, 0, 0)
        msgMesh.material = msgMaterial;
        msgMesh.setEnabled(false);
        this.badCoinsMsgs.push(msgMesh);
      }

      //LOAD COIN PLUS 1 SCORE
      const planeCoinPlus1Material = new BABYLON.StandardMaterial('coinPlus1Material', this.sceneGlobal);
      planeCoinPlus1Material.diffuseTexture = await this.LoadTextureAsync('./models/scores/Score_+1.png', this.sceneGlobal, false);
      planeCoinPlus1Material.transparencyMode = 2
      planeCoinPlus1Material.disableLighting = true
      planeCoinPlus1Material.diffuseTexture.hasAlpha = true
      planeCoinPlus1Material.useAlphaFromDiffuseTexture = true;
      planeCoinPlus1Material.emissiveColor = new BABYLON.Color3(1,1,1)
      this.coinScorePlus1 = BABYLON.MeshBuilder.CreateGround("coinplus1", { height: 0.3, width: 0.3 });
      this.coinScorePlus1.rotation = new Vector3(0.5 * Math.PI, 0, 0)
      this.coinScorePlus1.material = planeCoinPlus1Material;
      this.coinScorePlus1.setEnabled(false)

      //LOAD COIN PLUS 3 SCORE
      const planeCoinPlus3Material = new BABYLON.StandardMaterial('coinPlus3Material', this.sceneGlobal);
      planeCoinPlus3Material.diffuseTexture = await this.LoadTextureAsync('./models/scores/Score_+3.png', this.sceneGlobal, false);
      planeCoinPlus3Material.transparencyMode = 2
      planeCoinPlus3Material.disableLighting = true
      planeCoinPlus3Material.diffuseTexture.hasAlpha = true
      planeCoinPlus3Material.useAlphaFromDiffuseTexture = true;
      planeCoinPlus3Material.emissiveColor = new BABYLON.Color3(1,1,1)
      this.coinScorePlus3 = BABYLON.MeshBuilder.CreateGround("coinplus3", { height: 0.3, width: 0.3 });
      this.coinScorePlus3.rotation = new Vector3(0.5 * Math.PI, 0, 0)
      this.coinScorePlus3.material = planeCoinPlus3Material;
      this.coinScorePlus3.setEnabled(false)

      //LOAD COIN PLUS 5 SCORE
      const planeCoinPlus5Material = new BABYLON.StandardMaterial('coinPlus5Material', this.sceneGlobal);
      planeCoinPlus5Material.diffuseTexture = await this.LoadTextureAsync('./models/scores/Score_+5.png', this.sceneGlobal, false);
      planeCoinPlus5Material.transparencyMode = 2
      planeCoinPlus5Material.disableLighting = true
      planeCoinPlus5Material.diffuseTexture.hasAlpha = true
      planeCoinPlus5Material.useAlphaFromDiffuseTexture = true;
      planeCoinPlus5Material.emissiveColor = new BABYLON.Color3(1,1,1)
      this.coinScorePlus5 = BABYLON.MeshBuilder.CreateGround("coinplus5", { height: 0.3, width: 0.3 });
      this.coinScorePlus5.rotation = new Vector3(0.5 * Math.PI, 0, 0)
      this.coinScorePlus5.material = planeCoinPlus5Material;
      this.coinScorePlus5.setEnabled(false)

      //LOAD CHARACTER
      console.log("Loading character")
      {
        const { meshes, animationGroups } = await SceneLoader.ImportMeshAsync('', './models/', 'Character.gltf', this.sceneGlobal)
        this.characterRoot = meshes[0] as BABYLON.Mesh
        this.characterRoot.rotation = new Vector3(0, 0, 0)
        this.characterMesh = meshes[1] as BABYLON.Mesh

        this.characterAnimationIdle = animationGroups[0]
        this.characterAnimationWalk = animationGroups[1]
        this.characterAnimationRun = animationGroups[2]
        this.characterAnimationSlide = animationGroups[3]
        this.characterAnimationHit = animationGroups[4]
        this.characterAnimationJump = animationGroups[5]
        this.characterAnimationRun.play(true)

        /*if (this.shadowGenerator !== null) {
          this.shadowGenerator.addShadowCaster(this.characterRoot, true);
        }
        for (let m = 0; m < meshes.length; m++) {
          meshes[m].receiveShadows = true
        }*/

        const colliderMaterial = new BABYLON.StandardMaterial('characterMaterialCollider', this.sceneGlobal);
        colliderMaterial.diffuseColor = new BABYLON.Color3(0, 1, 0)
        colliderMaterial.transparencyMode = 2
        colliderMaterial.alpha = this.showBoundingBoxes ? 0.5 : 0
        const box = BABYLON.MeshBuilder.CreateBox("characterCollider", {}, this.sceneGlobal);
        box.scaling = new Vector3(1, 1, 1)
        box.position.y = 1
        box.material = colliderMaterial;
        this.characterCollider = box
        this.characterRoot.addChild(box)
      }

      //LOAD MODULES
      console.log("Loading modules")
      {
        var path = './models/Scenario/{PLATFORM}/'.replace("{PLATFORM}", isMobile() === true ? "Mobile":"Desktop");
        var modelFile = 'SisteCredito_Runner_v6.glb';
        
        const { meshes, animationGroups } = await SceneLoader.ImportMeshAsync('', path, modelFile, this.sceneGlobal)
        for (let a = 0; a < animationGroups.length; a++) {
          animationGroups[a].play(true)
        }
        this.modulesRoot = meshes[0] as BABYLON.Mesh

        const barrierMaterial = new BABYLON.StandardMaterial('barrierMaterialCollider', this.sceneGlobal);
        barrierMaterial.diffuseColor = new BABYLON.Color3(1, 0, 0)
        barrierMaterial.transparencyMode = 2
        barrierMaterial.alpha = this.showBoundingBoxes ? 0.8 : 0

        const coneMaterial = new BABYLON.StandardMaterial('coneMaterialCollider', this.sceneGlobal);
        coneMaterial.diffuseColor = new BABYLON.Color3(1, 1, 0)
        coneMaterial.transparencyMode = 2
        coneMaterial.alpha = this.showBoundingBoxes ? 0.8 : 0

        const vehicleMaterial = new BABYLON.StandardMaterial('vehicleMaterialCollider', this.sceneGlobal);
        vehicleMaterial.diffuseColor = new BABYLON.Color3(1, 0, 1)
        vehicleMaterial.transparencyMode = 2
        vehicleMaterial.alpha = this.showBoundingBoxes ? 0.8 : 0

        const coinMaterial = new BABYLON.StandardMaterial('coinMaterialCollider', this.sceneGlobal);
        coinMaterial.diffuseColor = new BABYLON.Color3(0, 1, 0)
        coinMaterial.transparencyMode = 2
        coinMaterial.alpha = this.showBoundingBoxes ? 0.2 : 0

        this.obstacles = [];
        this.coins = [];
        this.modules = [];

        for (let i = 0; i < this.modulesRoot.getChildren().length; i++) {
          const moduleItem = this.modulesRoot.getChildren()[i] as BABYLON.Mesh

          if (!moduleItem.name.toLocaleLowerCase().includes("modulo")) { //skip objects different to 'Modulo'
            continue;
          }
          moduleItem.setEnabled(false)
          this.modules.push(moduleItem)
          //coins
          var coinsParent = moduleItem.getChildren()[0]
          for (let cp = 0; cp < moduleItem.getChildren().length; cp++) {
            coinsParent = moduleItem.getChildren()[cp]
            if (coinsParent.name.toLocaleLowerCase().includes("coins"))
              break
          }
          for (let c = 0; c < coinsParent.getChildren().length; c++) {
            var coinItem = coinsParent.getChildren()[c] as BABYLON.Mesh
            coinItem.showBoundingBox = this.showBoundingBoxes
            const obstacleCoin = BABYLON.MeshBuilder.CreateBox("generatedCoin_M" + i + "_C" + c, {}, this.sceneGlobal);
            obstacleCoin.scaling = new Vector3(1, 3, 1)
            obstacleCoin.material = coinMaterial;
            coinItem.addChild(obstacleCoin)
            obstacleCoin.position = new Vector3(0, 0, 0)
            this.coins.push(obstacleCoin)
          }

          //obstacles
          var obstaclesParent = moduleItem.getChildren()[0]
          for (let op = 0; op < moduleItem.getChildren().length; op++) {
            obstaclesParent = moduleItem.getChildren()[op]
            if (obstaclesParent.name.toLocaleLowerCase().includes("obstacles"))
              break
          }

          for (let o = 0; o < obstaclesParent.getChildren().length; o++) {
            var obstacleItem = obstaclesParent.getChildren()[o] as BABYLON.Mesh
            var obstacleName = obstacleItem.name.toLowerCase()
            if (!obstacleName.includes("ignore")) {
              obstacleItem.showBoundingBox = this.showBoundingBoxes
              this.obstacles.push(obstacleItem)
            }
          }
        }

        this.GenerateInfiniteRoad(true)

        for (let m = 0; m < meshes.length; m++) {
          meshes[m].receiveShadows = true
        }

        //console.log(this.sceneGlobal);

        for (let m = 0; m < this.sceneGlobal.materials.length; m++) {
          var material = this.sceneGlobal.materials[m] as BABYLON.PBRMaterial
          material.metallic = 0.2
          material.roughness = 0.6 //0.5
          material.indexOfRefraction = 1
          material.metallicF0Factor = 1
        }

        console.log("modules loaded")
        this.props.setIsGameLoaded(true)
      }
      console.log("game loaded")
    }
  }

  GenerateInfiniteRoad = (firstTime: boolean) => {

    if (this.modulesRoot === null || this.modules === null)
      return;

    if (firstTime === true) {

      this.last3ModulesUsed = [0, 0, 0]
      this.modules[this.last3ModulesUsed[0]].setEnabled(true)
      this.modules[this.last3ModulesUsed[0]].position.z = 0
      //generate second module
      this.last3ModulesUsed[1] = this.NextModuleRandom(this.modules.length - 1)
      while (this.last3ModulesUsed[1] === this.last3ModulesUsed[0]) {
        this.last3ModulesUsed[1] = this.NextModuleRandom(this.modules.length - 1)
      }
      this.modules[this.last3ModulesUsed[1]].position.z = -this.infiniteRoadNextCheck
      this.modules[this.last3ModulesUsed[1]].setEnabled(true)

      //generate third module
      this.last3ModulesUsed[2] = this.NextModuleRandom(this.modules.length - 1)
      while (this.last3ModulesUsed[2] === this.last3ModulesUsed[0] || this.last3ModulesUsed[2] === this.last3ModulesUsed[1]) {
        this.last3ModulesUsed[2] = this.NextModuleRandom(this.modules.length - 1)
      }
      this.modules[this.last3ModulesUsed[2]].position.z = -(this.infiniteRoadNextCheck + 100)
      this.modules[this.last3ModulesUsed[2]].setEnabled(true)

      this.modulesRoot.position.z = this.infiniteRoadOffsetCheck
    } else if (this.modulesRoot.position.z > this.infiniteRoadNextCheck + this.infiniteRoadOffsetCheck) {
      this.characterSpeed += 2 // BALANCE
      if (this.characterSpeed > 200)
        this.characterSpeed = 200
      this.infiniteRoadNextCheck += 100
      this.modules[this.last3ModulesUsed[0]].setEnabled(false)
      this.last3ModulesUsed[0] = this.last3ModulesUsed[1]
      this.last3ModulesUsed[1] = this.last3ModulesUsed[2]
      this.last3ModulesUsed[2] = //generate third module
        this.last3ModulesUsed[2] = this.NextModuleRandom(this.modules.length - 1)
      while (this.last3ModulesUsed[2] === this.last3ModulesUsed[0] || this.last3ModulesUsed[2] === this.last3ModulesUsed[1]) {
        this.last3ModulesUsed[2] = this.NextModuleRandom(this.modules.length - 1)
      }
      this.modules[this.last3ModulesUsed[2]].position.z = -(this.infiniteRoadNextCheck + 100)
      this.modules[this.last3ModulesUsed[2]].setEnabled(true)

      //REGENERATE COINS
      var coinsParent = this.modules[this.last3ModulesUsed[2]].getChildren()[0]
      for (let cp = 0; cp < this.modules[this.last3ModulesUsed[2]].getChildren().length; cp++) {
        coinsParent = this.modules[this.last3ModulesUsed[2]].getChildren()[cp]
        if (coinsParent.name.toLocaleLowerCase().includes("coins"))
          break
      }
      for (let c = 0; c < coinsParent.getChildren().length; c++) {
        var coinItem = coinsParent.getChildren()[c] as BABYLON.Mesh
        coinItem.setEnabled(true)
      }
    }
  }

  NextModuleRandom = (maxValue: number) => {
    return Math.round(maxValue * Math.random())
  }

  AnimateCharacterHorizontally = () => {
    if (this.characterSideAnimationName === "free") {
      if (this.props.horizontalMovement === 1 && this.characterLane < 1) {//puede ir mas a la derecha
        this.characterSideAnimationName = "right"
        this.characterSideAnimationValue = 1
      } else if (this.props.horizontalMovement === -1 && this.characterLane > -1) {//puede ir mas a la izquierda
        this.characterSideAnimationName = "left"
        this.characterSideAnimationValue = -1
      }
    }
  }

  AnimateCharacterVerically = () => {

    if (this.characterVerticalAnimationName === "free" && this.characterCollider) {
      if (this.props.verticalMovement === 1) {//arriba
        this.characterAnimationJump?.stop()
        this.characterVerticalAnimationName = "jump"
        this.characterCollider.position.y = 2
        this.characterAnimationJump?.play(false)
        this.jumpSoundRef.current.currentTime = 0
        this.jumpSoundRef.current.play()
      } else if (this.props.verticalMovement === -1) {//abajo
        this.characterAnimationSlide?.stop()
        this.characterVerticalAnimationName = "duck"
        this.characterCollider.position.y = 0
        this.characterAnimationSlide?.play(false)
        this.slideSoundRef.current.currentTime = 0
        this.slideSoundRef.current.play()
      }
      setTimeout(() => {
        this.characterVerticalAnimationName = "free"
        if (this.characterCollider) this.characterCollider.position.y = 1
        this.characterAnimationWalk?.stop()
        this.characterAnimationSlide?.stop()
      }, 1000)
    }
  }

  frameRender(deltaTime: number): void {
    if (this.props.playGame !== true || this.props.gameOver === true || this.props.isWindowFocused === false)
      return
    if (!this.characterRoot || !this.modules || !this.modulesRoot || !this.obstacles || !this.characterCollider || !this.characterMesh || !this.cameraGlobal || !this.coins || !this.coinScorePlus1 || !this.coinScorePlus3 || !this.coinScorePlus5 || !this.goodCoinsMsgs || !this.badCoinsMsgs)
      return
    
    var removeFlyingScoresIndex: number[] = []
    for (let indexToRemove = 0; indexToRemove < this.activeFlyingScores.length; indexToRemove++) {
      this.activeFlyingScores[indexToRemove].timeLeft -= deltaTime
      this.activeFlyingScores[indexToRemove].mesh.position = this.activeFlyingScores[indexToRemove].coin.absolutePosition.clone().add(msgOffset);

      if(this.activeFlyingScores[indexToRemove].timeLeft < 0){
        removeFlyingScoresIndex.push(indexToRemove)
      }
    }
    removeFlyingScoresIndex = removeFlyingScoresIndex.reverse()
    for (let removeIndex = 0; removeIndex < removeFlyingScoresIndex.length; removeIndex++) {
      this.activeFlyingScores[removeIndex].mesh.dispose()
      this.activeFlyingScores.splice(removeIndex, 1)
    }

    this.totalGameTime += deltaTime
    this.props.setUserTime(Math.floor(this.totalGameTime))

    this.characterRoot.position.x += 4 * this.characterSideAnimationValue * this.characterSideDistance * deltaTime  * this.characterSpeed / this.characterBaseSpeed;
    this.cameraGlobal.position.x = this.characterRoot.position.x
    this.characterRoot.rotation = new Vector3(0, - 30 * Math.PI * this.characterSideAnimationValue / 180, 0)

    //verrify horizontally
    if (this.characterSideAnimationName === "right") {
      if (this.characterLane === 0 && this.characterRoot.position.x > this.characterSideDistance) {
        this.characterSideAnimationName = "free"
        this.characterRoot.position.x = this.characterSideDistance
        this.characterSideAnimationValue = 0
        this.characterLane = 1
      } else if (this.characterLane === -1 && this.characterRoot.position.x > 0) {
        this.characterSideAnimationName = "free"
        this.characterRoot.position.x = 0
        this.characterSideAnimationValue = 0
        this.characterLane = 0
      }
    }
    if (this.characterSideAnimationName === "left") {
      if (this.characterLane === 0 && this.characterRoot.position.x < -this.characterSideDistance) {
        this.characterSideAnimationName = "free"
        this.characterRoot.position.x = -this.characterSideDistance
        this.characterSideAnimationValue = 0
        this.characterLane = -1
      } else if (this.characterLane === 1 && this.characterRoot.position.x < 0) {
        this.characterSideAnimationName = "free"
        this.characterRoot.position.x = 0
        this.characterSideAnimationValue = 0
        this.characterLane = 0
      }
    }

    this.characterCollider.computeWorldMatrix(true);
    if (this.characterSpeed > this.characterBaseSpeed) {//reached minimun speed
      this.characterMesh.setEnabled(true)
      for (let o = 0; o < this.obstacles.length; o++) {
        var obstacleItem = this.obstacles[o]
        obstacleItem.computeWorldMatrix(true);
        if (obstacleItem.isEnabled() === true && this.characterCollider.intersectsMesh(obstacleItem, true)) {
          this.characterSpeed = -4//-10 BALANCE
          this.failSoundRef.current.currentTime = 0
          this.failSoundRef.current.play()
          this.characterAnimationHit?.play(false)
          //////////////DISMINUIR UNA VIDA Y/O GAME OVER
          this.totalLife -= 1
          this.props.setUserLifes(this.totalLife)
          if(this.totalLife < 1){
            this.winSoundRef.current.currentTime = 0
            this.winSoundRef.current.play()
          }
        }
      }
    } else {
      this.invincibleAnimation -= deltaTime
      if (this.invincibleAnimation < 0) {
        this.invincibleAnimation = 0.2
        this.characterMesh.setEnabled(!this.characterMesh.isEnabled())
        this.characterSpeed += 1//4 BALANCE
      }
    }

    for (let c = 0; c < this.coins.length; c++) {
      const coinItem = this.coins[c]
      coinItem.computeWorldMatrix(true);
      if (coinItem.isEnabled() === true && this.characterCollider.intersectsMesh(coinItem, true) && coinItem.parent !== null) {
        //////////////SUBIR PUNTAJE
        coinItem.parent.setEnabled(false)
        let points = 10
        
        const goodMsg = this.goodCoinsMsgs[Math.floor(Math.random() * this.goodCoinsMsgs.length)];
        const badMsg = this.badCoinsMsgs[Math.floor(Math.random() * this.badCoinsMsgs.length)];

        if (coinItem.parent.name.toLowerCase().includes("mid")) {
          points = 30;
          const flyingScorePlus3 : FlyingScore = { timeLeft: 1, mesh : goodMsg.clone(), coin: coinItem }
          flyingScorePlus3.mesh.setEnabled(true)
          flyingScorePlus3.mesh.position = coinItem.absolutePosition.clone().add(msgOffset);
          this.activeFlyingScores.push(flyingScorePlus3)
        } else if (coinItem.parent.name.toLowerCase().includes("big")) {
          points = 50;
          const flyingScorePlus5 : FlyingScore = { timeLeft: 1, mesh : goodMsg.clone(), coin: coinItem }
          flyingScorePlus5.mesh.setEnabled(true)
          flyingScorePlus5.mesh.position = coinItem.absolutePosition.clone().add(msgOffset);
          this.activeFlyingScores.push(flyingScorePlus5)
        } else if (coinItem.parent.name.toLowerCase().includes("bad")) {
          points = -10;
          const flyingScoreBad : FlyingScore = { timeLeft: 1, mesh : badMsg.clone(), coin: coinItem }
          flyingScoreBad.mesh.setEnabled(true)
          flyingScoreBad.mesh.position = coinItem.absolutePosition.clone().add(msgOffset);
          this.activeFlyingScores.push(flyingScoreBad)
        } else{
          const flyingScorePlus1 : FlyingScore = { timeLeft: 1, mesh : goodMsg.clone(), coin: coinItem}
          flyingScorePlus1.mesh.setEnabled(true)
          flyingScorePlus1.mesh.position = coinItem.absolutePosition.clone().add(msgOffset);
          this.activeFlyingScores.push(flyingScorePlus1)
        }
        if(points > 0){
          this.coinSoundRef.current.currentTime = 0;
          this.coinSoundRef.current.play();
        }else{
          this.failSoundRef.current.currentTime = 0;
          this.failSoundRef.current.play();
        }
        this.totalScore += points
        this.props.setUserScore(this.totalScore)
      }
    }

    this.modulesRoot.position.z += this.characterSpeed * deltaTime;
    this.GenerateInfiniteRoad(false)

    //PERFORMANCE CONTROL
    this.globalPerformanceControl.avgFrameTime += deltaTime
    this.globalPerformanceControl.framesCount += 1
    if (this.engineGlobal && this.globalPerformanceControl.framesCount > this.globalPerformanceControl.frameSamples - 1) {
      const avgFrameRate = this.globalPerformanceControl.framesCount / this.globalPerformanceControl.avgFrameTime;
      this.globalPerformanceControl.framesCount = 0
      this.globalPerformanceControl.avgFrameTime = 0

      const scaleChage = avgFrameRate > this.globalPerformanceControl.targetFramerate ? -0.1 : 0.1;
      this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.scalingLevel + scaleChage
      if (this.globalPerformanceControl.scalingLevel > this.globalPerformanceControl.maxScalingLevel)
        this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.maxScalingLevel
      if (this.globalPerformanceControl.scalingLevel < this.globalPerformanceControl.minScalingLevel)
        this.globalPerformanceControl.scalingLevel = this.globalPerformanceControl.minScalingLevel

      if (this.globalPerformanceControl.scalingLevel !== this.engineGlobal.getHardwareScalingLevel()) {
        this.engineGlobal.setHardwareScalingLevel(this.globalPerformanceControl.scalingLevel);
      }
    }
  }

  render(): React.ReactNode {
    return (
      <div className={`scene3d-container ${this.props.isVisible === true ? 'active' : 'hidden'} ${(this.props.playGame === true) ? 'focus' : ''}`}>
        <canvas ref={this.canvasRef} style={{ width: '100%', height: '100%' }}>
          <ReactAudioPlayer src={require("../assets/sounds/runner_1.mp3")} autoPlay loop controls={false} muted={this.props.soundMuted} id={"backgroundMusic"} volume={0.2} />
          <audio ref={this.coinSoundRef} src={require("../assets/sounds/Sound_Positive.mp3")} />
          <audio ref={this.failSoundRef} src={require("../assets/sounds/Sound_Fail.mp3")} />
          <audio ref={this.jumpSoundRef} src={require("../assets/sounds/Sound_Jump.mp3")} />
          <audio ref={this.slideSoundRef} src={require("../assets/sounds/Sound_Swipe.mp3")} />
          <audio ref={this.winSoundRef} src={require("../assets/sounds/Sound_Win.mp3")} />
        </canvas>
      </div>
    )
  }
}

export default Scene3D